import 'whatwg-fetch'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import reduce from 'lodash/reduce'

import * as contentTypes from './contentTypes'
import { RequestError, InternalError, ApiError } from './errors'

const EMPTY_CODES = [204]

const FETCH_DEFAULTS = {
  method: 'GET',
  headers: {
    Accept: contentTypes.JSON_TYPE,
    'Content-Type': contentTypes.JSON_TYPE
  }
}

/**
 * Returns full api url request with apiBaseUrl and queryString
 *
 * @param {String} endpoint - endpoint to make request
 * @param {Object} [queryParams] - query params to make query string
 * @param {Sting} [baseUrl=process.env.API_BASE_URL] - base url to prepend the api call
 * @return {String} ful request url
 */
function getRequestURL(endpoint, queryParams, baseUrl = '') {
  const base = isEmpty(baseUrl) ? process.env.REACT_APP_API_BASE_URL || baseUrl : baseUrl
  const url = endpoint.startsWith(base) ? endpoint : `${base}${endpoint}`
  const queryString = reduceQueryParams(queryParams)

  return `${url}${queryString}`
}

/**
 * reduce query params to query string
 *
 * @param {Object} queryParams - key value query params
 * @return {String} formed query string
 */
function reduceQueryParams(queryParams) {
  if (isEmpty(queryParams)) return ''

  return reduce(queryParams, (queryString, value, param) => {
    if (isNil(value)) return queryString

    const encodedParam = encodeURIComponent(param)
    const encodedValue = encodeURIComponent(value)
    return `${queryString}${encodedParam}=${encodedValue}&`
  }, '?').slice(0, -1) // remove last &
}

/**
 * parse passed options and prepare for request
 *
 * @param {Object} options - options to make final options
 * @param {String} [options.method="GET"] - http request method
 * @param {Object} [options.headers={
 *  Content-Type: 'application/json',
 *  Accept: 'application/json'
 * }] - headers to send request
 * @param {*} [options.body] - request body
 * @param {...options} - more options to merge in request
 * @return {Object} parsed options
 */
function parseOptions({
  method,
  headers,
  body,
  ...options
}) {
  const opts = {
    method: method || FETCH_DEFAULTS.method,
    headers: new Headers({
      ...FETCH_DEFAULTS.headers,
      ...headers
    }),
    ...options
  }
  const contentType = opts.headers.get('Content-Type')

  // If is multipart form data rquest we need to delete header Content-Type
  // for the browser autogenerates `boundary` parameter which identifies
  // the boundary name between parts in this multipart request
  if (contentTypes.isMultipartFormData(contentType)) {
    opts.headers.delete('Content-Type')
  }
  if (body) {
    opts.body = prepareRequestBody(body, contentType)
  }

  return opts
}

/**
 * prepare the request body by content type
 *
 * @param {*} body - request raw body
 * @param {String} contentType - request header Content-Type
 * @return {*} parsed body
 */
function prepareRequestBody(body, contentType) {
  if (contentTypes.isJSONType(contentType)) {
    return JSON.stringify(body)
  }

  return body
}

/** response parsers by conent type */
const responseParsers = {
  [contentTypes.JSON_TYPE](response) {
    return response.json()
  },
  [contentTypes.TEXT_TYPE](response) {
    return response.text()
  },
  [contentTypes.CSV_TYPE](response) {
    return response.text()
  },
  [contentTypes.ARRAY_BUFFER_TYPE](response) {
    return response.arrayBuffer()
  }
}

/**
 * Converts response to parsed data by contentType
 *
 * @async
 * @throws {InternalError} if parses fails
 * @param {Response} response - response
 * @return {Promise} parsed response data or response
 */
async function parseResponse(response) {
  if (EMPTY_CODES.includes(response.status)) {
    return
  }

  try {
    // content type can come with with charset after a semicolon
    // because of this we use split(;) and destructure the first part
    const [contentType] = response.headers.get('Content-Type').split(';')
    let data = response

    if (responseParsers.hasOwnProperty(contentType)) {
      data = await responseParsers[contentType](response)
    }

    return data
  } catch (error) {
    throw new InternalError(error.message)
  }
}

/**
 * process the response data
 *
 * @async
 * @throws {ApiError} if response is not ok
 * @param {Response} response - response of fetch api
 * @return {*} parsed response
 */
async function processResponse(response) {
  const data = await parseResponse(response)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, data)
  }

  return data
}

/**
 * send api request
 *
 * @async
 * @throws {RequestError} if fail to make request
 * @return {Promise<Response>} response of fetch request
 * @see {@link request} to see options documentation
 */
async function makeRequest(endpoint, options = {}) {
  try {
    const { baseUrl, query, ...opts } = options
    const url = getRequestURL(endpoint, query, baseUrl)
    const requestOptions = parseOptions(opts)
    const response = await fetch(url, requestOptions)

    return response
  } catch (error) {
    throw new RequestError(error.message)
  }
}

/**
 * make api request and proccess the response
 *
 * @async
 * @param {String} endpoint - to make api request
 * @param {Object} [options={}] - options to fetch
 * @param {String} [options.baseUrl] - api base url
 * @param {Object} [options.query] - key value query params
 * @param {String} [options.method] - http method to send in request
 * @param {Object} [options.headers] - headers to send in request
 * @param {*} [options.body] - body to send in request
 * @param {String} [options.credentials] - authentication credential mode to request
 * @return {Promise} fetch promise
 *
 * @see {@link https://github.github.io/fetch/ whatwg-fetch} to see fetch specification
 */
export default async function request(endpoint, options) {
  const response = await makeRequest(endpoint, options)
  const data = await processResponse(response)

  return data
}
